import { GMBH_IN_IG_LEGAL_CODE, UG_IN_IG_LEGAL_CODE } from '@repo/shared-kit/constants';
import type { AvailableLegalCountryCode } from '@repo/shared-config/app/constants/countries';
import { EUROPEAN_STANDARD_REGISTER_COUNTRIES } from '@repo/shared-kit/constants/countries';
/*
 * TODO: Constants below are duplicated from business-account/ and organizations/ to avoid
 * cross-domain relative imports that break the Embroider/webpack build for Ember apps.
 * They are planned to be migrated to a shared location separately.
 */

const ORGANIZATION_TYPES = {
  FREELANCER: 'freelancer',
  SMALL_BUSINESS: 'small_business',
  COMPANY: 'company',
  BIG_COMPANY: 'big_company',
  ASSOCIATION: 'association',
  WORKS_COUNCIL: 'works_council',
} as const;

function isFreelancerInitialOrganizationType({
  initialOrganizationType,
}: {
  initialOrganizationType?: string;
}): boolean {
  return initialOrganizationType === ORGANIZATION_TYPES.FREELANCER;
}

const NATURE_OF_OPERATIONS_OPTIONS = {
  CORE_BANKING: 'core_banking',
  INVOICING: 'invoicing',
  SPEND_MANAGEMENT: 'spend_management',
  BOOK_KEEPING: 'book_keeping',
  REMUNERATION: 'remuneration',
  CASHFLOW_MANAGEMENT: 'cashflow_management',
  PAYROLL_REIMBURSEMENT: 'payroll_reimbursement',
} as const;

const ANNUAL_TURNOVER_OPTIONS = [
  '0-50000',
  '50000-100000',
  '100000-200000',
  '200000-500000',
  '500000-1000000',
  '1000000-2000000',
  '2000000-5000000',
  '5000000-10000000',
  '10000000-50000000',
  '50000000-',
] as const;

const YEARLY_TRANSACTION_VOLUME_OPTIONS = [
  '0-10000',
  '10000-50000',
  '50000-100000',
  '100000-500000',
  '500000-',
] as const;

const GERMAN_BEC_ELIGIBLE_COMPANY_TYPES = [
  'DE107',
  'DE103',
  'DE201',
  'DE206',
  'DE202',
  'DE112',
  'DE108',
] as const;

// --- PricingOrganization type ---

export interface PricingOrganization {
  id: string;
  legalCountry: AvailableLegalCountryCode;
  legalFormCreditsafeCode?: string | null;
  underRegistration?: boolean | null;
  companyCreationFromScratch?: boolean | null;
  annualTurnover?: string | null;
  estimatedAnnualTurnover?: number | null;
  yearlyTransactionVolume?: string | null;
  maxSinglePaymentAmount?: number | null;
  natureOfOperations?: readonly string[];
  initialOrganizationType?: string | null;
}

const DE_FREELANCER_LEGAL_CODES = ['DE001', 'DE002', 'DE210'] as const;
const HIGH_ACTIVITY_TURNOVER_OPTIONS = ANNUAL_TURNOVER_OPTIONS.slice(2); // min >= 100k
const HIGH_ACTIVITY_TRANSACTION_VOLUME_OPTIONS = YEARLY_TRANSACTION_VOLUME_OPTIONS.slice(2); // min >= 50k
const HIGH_ACTIVITY_MAX_SINGLE_PAYMENT_AMOUNT = 10000;

export function isHighActivityDeFreelancer(organization: PricingOrganization): boolean {
  if (organization.legalCountry !== 'DE') return false;
  if (
    !DE_FREELANCER_LEGAL_CODES.includes(
      // oxlint-disable-next-line typescript/consistent-type-assertions
      organization.legalFormCreditsafeCode as (typeof DE_FREELANCER_LEGAL_CODES)[number]
    )
  ) {
    return false;
  }

  const { annualTurnover, yearlyTransactionVolume, maxSinglePaymentAmount } = organization;

  return (
    includesNullable(HIGH_ACTIVITY_TRANSACTION_VOLUME_OPTIONS, yearlyTransactionVolume) ||
    includesNullable(HIGH_ACTIVITY_TURNOVER_OPTIONS, annualTurnover) ||
    Number(maxSinglePaymentAmount) >= HIGH_ACTIVITY_MAX_SINGLE_PAYMENT_AMOUNT
  );
}

// --- Turnover utils ---

function getAnnualTurnoverOptions(): readonly string[] {
  return ANNUAL_TURNOVER_OPTIONS;
}

function getAnnualTurnoverMinMax(options: readonly string[]) {
  const min = Math.min(...options.map(option => Number(option.split('-')[0])));
  const max = Math.max(
    ...options.map(option => {
      const maxVal = option.split('-')[1];
      return maxVal ? Number(maxVal) : Infinity;
    })
  );
  return { min, max };
}

function getTurnoverData(sliceIndex: number) {
  const options = getAnnualTurnoverOptions().slice(sliceIndex);
  const { min, max } = getAnnualTurnoverMinMax(options);
  return { options, min, max };
}

function isWithinTurnoverRange(
  value: number | null | undefined,
  min: number,
  max: number
): boolean {
  if (value === null || value === undefined) return false;
  return value >= min && value <= max;
}

function includesNullable(arr: readonly string[], value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  return arr.includes(value);
}

// --- Computed properties ---

export function isEuropeanStandardRegisterCountry(legalCountry: string): boolean {
  return EUROPEAN_STANDARD_REGISTER_COUNTRIES.includes(
    // oxlint-disable-next-line typescript/consistent-type-assertions
    legalCountry as Parameters<typeof EUROPEAN_STANDARD_REGISTER_COUNTRIES.includes>[0]
  );
}

export function isDeFullCompanyCreation(organization: PricingOrganization): boolean {
  return (
    organization.legalCountry === 'DE' &&
    organization.underRegistration === true &&
    organization.companyCreationFromScratch === true &&
    organization.legalFormCreditsafeCode !== null &&
    organization.legalFormCreditsafeCode !== undefined &&
    [GMBH_IN_IG_LEGAL_CODE, UG_IN_IG_LEGAL_CODE].includes(organization.legalFormCreditsafeCode)
  );
}

export function isEligibleForRecommendedPlans(organization: PricingOrganization): boolean {
  const { annualTurnover, estimatedAnnualTurnover, yearlyTransactionVolume } = organization;
  const isSmallBusiness =
    organization.initialOrganizationType === ORGANIZATION_TYPES.SMALL_BUSINESS;
  const hasSelectedSpendManagement =
    organization.natureOfOperations?.includes(NATURE_OF_OPERATIONS_OPTIONS.SPEND_MANAGEMENT) ??
    false;

  const { options: optionsIt, min: minIt, max: maxIt } = getTurnoverData(6);
  const isItalyEligible =
    organization.legalCountry === 'IT' &&
    isSmallBusiness &&
    (includesNullable(optionsIt, annualTurnover) ||
      isWithinTurnoverRange(estimatedAnnualTurnover, minIt, maxIt) ||
      includesNullable(YEARLY_TRANSACTION_VOLUME_OPTIONS.slice(3), yearlyTransactionVolume) ||
      hasSelectedSpendManagement);

  const { options: optionsEs, min: minEs, max: maxEs } = getTurnoverData(6);
  const isSpainEligible =
    organization.legalCountry === 'ES' &&
    isSmallBusiness &&
    (includesNullable(optionsEs, annualTurnover) ||
      isWithinTurnoverRange(estimatedAnnualTurnover, minEs, maxEs) ||
      includesNullable(YEARLY_TRANSACTION_VOLUME_OPTIONS.slice(3), yearlyTransactionVolume) ||
      hasSelectedSpendManagement);

  const { options: optionsFr, min: minFr, max: maxFr } = getTurnoverData(2);
  const isFranceEligible =
    organization.legalCountry === 'FR' &&
    isSmallBusiness &&
    (includesNullable(optionsFr, annualTurnover) ||
      isWithinTurnoverRange(estimatedAnnualTurnover, minFr, maxFr) ||
      includesNullable(YEARLY_TRANSACTION_VOLUME_OPTIONS.slice(2), yearlyTransactionVolume) ||
      hasSelectedSpendManagement);

  const { options: optionsDe, min: minDe, max: maxDe } = getTurnoverData(5);
  const isGermanyEligible =
    organization.legalCountry === 'DE' &&
    includesNullable(GERMAN_BEC_ELIGIBLE_COMPANY_TYPES, organization.legalFormCreditsafeCode) &&
    (includesNullable(optionsDe, annualTurnover) ||
      isWithinTurnoverRange(estimatedAnnualTurnover, minDe, maxDe) ||
      includesNullable(YEARLY_TRANSACTION_VOLUME_OPTIONS.slice(3), yearlyTransactionVolume) ||
      hasSelectedSpendManagement);

  return isItalyEligible || isSpainEligible || isFranceEligible || isGermanyEligible;
}

export function isEligibleForSoloRecommendedPlans(organization: PricingOrganization): boolean {
  const { annualTurnover, estimatedAnnualTurnover, yearlyTransactionVolume } = organization;
  const hasBookKeepingAndInvoicing = [
    NATURE_OF_OPERATIONS_OPTIONS.BOOK_KEEPING,
    NATURE_OF_OPERATIONS_OPTIONS.INVOICING,
  ].every(val => organization.natureOfOperations?.includes(val) ?? false);

  const { options: optionsFr, min: minFr, max: maxFr } = getTurnoverData(2);
  const isFranceEligible =
    organization.legalCountry === 'FR' &&
    isFreelancerOrg(organization) &&
    (hasBookKeepingAndInvoicing ||
      includesNullable(optionsFr, annualTurnover) ||
      isWithinTurnoverRange(estimatedAnnualTurnover, minFr, maxFr) ||
      includesNullable(YEARLY_TRANSACTION_VOLUME_OPTIONS.slice(2), yearlyTransactionVolume));

  const isItalyEligible =
    organization.legalCountry === 'IT' &&
    isFreelancerOrg(organization) &&
    hasBookKeepingAndInvoicing;

  const isSpainEligible =
    organization.legalCountry === 'ES' &&
    isFreelancerOrg(organization) &&
    hasBookKeepingAndInvoicing;

  return isFranceEligible || isItalyEligible || isSpainEligible;
}

function isFreelancerOrg(organization: PricingOrganization): boolean {
  return isFreelancerInitialOrganizationType({
    initialOrganizationType: organization.initialOrganizationType ?? undefined,
  });
}
